import { createTheme, alpha } from '@mui/material/styles'

const MONO = '"Geist Mono", "Courier New", monospace'
const SANS = '"Inter", system-ui, -apple-system, sans-serif'

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#18181b',    // zinc-900
            light: '#3f3f46',   // zinc-700
            dark: '#09090b',    // zinc-950
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#71717a',    // zinc-500 — muted, no magenta
            light: '#a1a1aa',   // zinc-400
            dark: '#52525b',    // zinc-600
            contrastText: '#ffffff',
        },
        success: {
            main: '#16a34a',    // green-700
            light: '#22c55e',
            contrastText: '#fff',
        },
        error: {
            main: '#dc2626',    // red-600
            light: '#ef4444',
            contrastText: '#fff',
        },
        warning: {
            main: '#d97706',
            light: '#f59e0b',
            contrastText: '#fff',
        },
        background: {
            default: '#f4f4f5', // zinc-100
            paper: '#ffffff',
        },
        text: {
            primary: '#18181b',
            secondary: '#71717a',
        },
        divider: 'rgba(0,0,0,0.08)',
    },
    typography: {
        fontFamily: SANS,
        fontSize: 13,
        h4: { fontWeight: 600, letterSpacing: '-0.02em' },
        h5: { fontWeight: 600, letterSpacing: '-0.01em' },
        h6: { fontWeight: 600 },
        body1: { fontSize: '0.875rem' },
        body2: { fontSize: '0.8125rem' },
        caption: { fontSize: '0.75rem', color: '#71717a' },
        button: {
            fontFamily: SANS,
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0',
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: '7px 16px',
                    gap: '6px',
                    borderRadius: 6,
                    fontWeight: 500,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': { backgroundColor: theme.palette.primary.light },
                    '&:active': { backgroundColor: theme.palette.primary.dark },
                    '&.Mui-disabled': {
                        backgroundColor: '#e4e4e7',
                        color: '#a1a1aa',
                    },
                }),
                outlined: ({ theme }) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'transparent',
                    color: theme.palette.text.primary,
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderColor: theme.palette.primary.light,
                    },
                }),
                sizeSmall: {
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    color: '#71717a',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)', color: '#18181b' },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    boxShadow: 'none',
                    border: '1px solid rgba(0,0,0,0.08)',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: { padding: '24px', '&:last-child': { paddingBottom: '24px' } },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    backgroundImage: 'none',
                },
                outlined: {
                    border: '1px solid rgba(0,0,0,0.08)',
                },
                elevation1: {
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        backgroundColor: '#f4f4f5',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: '#71717a',
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                        paddingTop: 10,
                        paddingBottom: 10,
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                    '&:last-child td': { borderBottom: 0 },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    padding: '10px 16px',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    height: 22,
                    borderRadius: 4,
                },
                label: { paddingLeft: 8, paddingRight: 8 },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: { fontSize: '0.8125rem', minHeight: 36 },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: '0.8125rem' },
                input: { fontSize: '0.8125rem' },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { fontSize: '0.8125rem' },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: { borderColor: 'rgba(0,0,0,0.15)' },
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.3)' },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: 'none',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: 'rgba(0,0,0,0.07)' },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.75rem',
                    borderRadius: 6,
                    backgroundColor: '#18181b',
                    padding: '5px 10px',
                },
            },
        },
    },
})

export { MONO, SANS }
export default theme
